import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

// Hook accepts sessionId (current session), optionally relationshipSessionIds (all sessions for this relationship),
// and participants array for multi-tenant access control
// relationshipSessionIds is used to filter carryover items to only those from the same relationship
export function useActionItems(sessionId = null, relationshipSessionIds = null, participants = null) {
  const { user } = useAuth();
  const [actionItems, setActionItems] = useState([]);
  const [allActionItems, setAllActionItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get action items where user is a participant (multi-tenant)
  useEffect(() => {
    if (!user?.uid) {
      setAllActionItems([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'actionItems'),
      where('participants', 'array-contains', user.uid),
      orderBy('dueDate', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const itemsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllActionItems(itemsData);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useActionItems] listener error:', err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Filter for current session
  useEffect(() => {
    // Only show items if we have a valid sessionId
    if (sessionId && typeof sessionId === 'string') {
      setActionItems(allActionItems.filter(item => item.sessionId === sessionId));
    } else {
      setActionItems([]);
    }
  }, [sessionId, allActionItems]);

  const addActionItem = async (data) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add action items');
    }
    if (!sessionId || typeof sessionId !== 'string') {
      throw new Error('Cannot add action item without a valid session');
    }
    if (!participants || participants.length === 0) {
      throw new Error('Cannot add action item without participants');
    }

    await addDoc(collection(db, 'actionItems'), {
      ...data,
      sessionId,
      participants, // Multi-tenant: who can see this action item
      createdByUid: user.uid, // Multi-tenant: who created this item
      // assignedToUid should be passed in data
      followUpDate: data.followUpDate || null,
      completed: false,
      createdAt: new Date()
    });
  };

  const updateActionItem = async (itemId, data) => {
    await updateDoc(doc(db, 'actionItems', itemId), {
      ...data,
      updatedAt: new Date()
    });
  };

  const deleteActionItem = async (itemId) => {
    await deleteDoc(doc(db, 'actionItems', itemId));
  };

  const toggleCompleted = async (itemId, currentValue) => {
    await updateDoc(doc(db, 'actionItems', itemId), {
      completed: !currentValue
    });
  };

  // Check if current user can edit/delete an action item
  const canEdit = useCallback((item) => {
    if (!user?.uid) return false;
    // User can edit if they created this item
    if (item.createdByUid) {
      return item.createdByUid === user.uid;
    }
    // Fallback for legacy data
    return false;
  }, [user?.uid]);

  // Get incomplete items from previous sessions (carryover)
  // If relationshipSessionIds is provided, only include items from sessions in that relationship
  const getCarryoverItems = (currentSessionId) => {
    return allActionItems.filter(item => {
      // Must be incomplete and not from current session
      if (item.completed || item.sessionId === currentSessionId) {
        return false;
      }
      // If we have relationship session IDs, only include items from those sessions
      if (relationshipSessionIds && relationshipSessionIds.length > 0) {
        return relationshipSessionIds.includes(item.sessionId);
      }
      // Fallback for legacy behavior (no relationship filtering)
      return true;
    });
  };

  // Check if item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate + 'T00:00:00');
    return due < today;
  };

  // Check if follow-up date is today
  const isFollowUpToday = (followUpDate) => {
    if (!followUpDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return followUpDate === today;
  };

  // Get action items with follow-up due today (for Tasks page)
  const getTodayFollowUps = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    return allActionItems.filter(item =>
      item.followUpDate === today && !item.completed
    );
  }, [allActionItems]);

  // Check if item is due within next 90 days
  const isDueWithin90Days = (dueDate) => {
    if (!dueDate) return true; // Show items without due dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate + 'T00:00:00');
    const ninetyDaysFromNow = new Date(today);
    ninetyDaysFromNow.setDate(today.getDate() + 90);
    return due <= ninetyDaysFromNow;
  };

  // Filter action items to only those due within 90 days (or overdue)
  const actionItemsWithin90Days = actionItems.filter(item =>
    isDueWithin90Days(item.dueDate) || isOverdue(item.dueDate)
  );

  return {
    actionItems: actionItemsWithin90Days,
    allActionItems,
    loading,
    addActionItem,
    updateActionItem,
    deleteActionItem,
    toggleCompleted,
    canEdit,
    getCarryoverItems,
    isOverdue,
    isFollowUpToday,
    getTodayFollowUps
  };
}
